import React from "react";
import { FileText, Download, Loader2, CheckCircle, AlertCircle, CloudCheck, Send, Plus } from "lucide-react";
import { Button } from "../../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { toast } from "sonner";
import { ConfirmDialog } from "../../ui/confirm-dialog";
import cedenteDocumentoService from "../../../services/cedenteDocumento.service";
import { processService } from "../../../services/process.service";

function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleDateString("pt-BR");
}

export default function CedenteDocumentosPanel({ cedenteId, processoId, processoCedenteId, status, onDocumentosChanged }) {
    const [docs, setDocs] = React.useState([]);
    const [crmCardId, setCrmCardId] = React.useState(null);
    const [pipelineStatuses, setPipelineStatuses] = React.useState({});
    const [selectedStatus, setSelectedStatus] = React.useState("");
    const [loading, setLoading] = React.useState(true);
    const [crmLoading, setCrmLoading] = React.useState(false);
    const [crmMsg, setCrmMsg] = React.useState(null);
    const [sentIds, setSentIds] = React.useState(new Set());
    const [showStatusConfirm, setShowStatusConfirm] = React.useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = React.useState(false);

    const loadDocs = React.useCallback((clearMsg = true) => {
        setLoading(true);
        if (clearMsg) setCrmMsg(null);
        cedenteDocumentoService
            .getDocsByCedente(cedenteId, processoCedenteId)
            .then((data) => {
                setDocs(data.docs || []);
                setCrmCardId(data.crm_card_id || null);
                const statuses = data.pipeline_statuses || {};
                setPipelineStatuses(statuses);
                const keys = Object.keys(statuses).filter(k => k !== "status_inicial");
                if (keys.length > 0 && !selectedStatus) setSelectedStatus(statuses[keys[0]]);
            })
            .catch(() => {
                setDocs([]);
                setCrmCardId(null);
            })
            .finally(() => setLoading(false));
    }, [cedenteId, processoCedenteId]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        loadDocs();
    }, [loadDocs]);

    const handleDownload = async (docId) => {
        try {
            const url = await cedenteDocumentoService.getDownloadUrl(docId);
            window.open(url, "_blank", "noopener,noreferrer");
        } catch {
        }
    };

    const handleCriarCard = async () => {
        setCrmLoading(true);
        setCrmMsg(null);
        try {
            const res = await cedenteDocumentoService.criarCardCrm(cedenteId, processoCedenteId);
            if (res.success && res.crm_card_id) {
                setCrmCardId(res.crm_card_id);
                setCrmMsg({ type: "success", text: `Card criado: ${res.crm_card_id}` });
                onDocumentosChanged();
            } else {
                setCrmMsg({ type: "error", text: res.message || "Erro ao criar card no CRM." });
            }
        } catch (e) {
            const d = e?.response?.data?.detail;
            setCrmMsg({ type: "error", text: typeof d === "string" ? d : "Erro ao criar card no CRM." });
        } finally {
            setCrmLoading(false);
        }
    };

    const handleEnviarDocs = async () => {
        setCrmLoading(true);
        setCrmMsg(null);
        try {
            const res = await cedenteDocumentoService.enviarDocsCrm(cedenteId, processoCedenteId, selectedStatus || null);
            if (res.success) {
                setCrmMsg({ type: "success", text: "Documentos enviados ao CRM" });
                setSentIds(prev => new Set([...prev, ...docs.map(d => d.id)]));
                
                // Pós envio: se estiver em análise, perguntar se deseja remover
                if (status === "analise") {
                    setShowStatusConfirm(true);
                }
            } else {
                setCrmMsg({ type: "error", text: res.message || "Falha ao enviar documentos." });
            }
        } catch (e) {
            const d = e?.response?.data?.detail;
            setCrmMsg({ type: "error", text: typeof d === "string" ? d : "Erro ao enviar documentos." });
        } finally {
            setCrmLoading(false);
        }
    };

    const handleConfirmStatusChange = async () => {
        setIsUpdatingStatus(true);
        try {
            await processService.linkCedente(processoId, {
                cedente_id: cedenteId,
                status: "pendente",
            });
            onDocumentosChanged();
            setShowStatusConfirm(false);
            toast.success("Status atualizado para Pendente");
        } catch (err) {
            toast.error("Erro ao atualizar status: " + (err.response?.data?.detail || err.message));
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const moveStatuses = Object.entries(pipelineStatuses).filter(([k]) => k !== "status_inicial");

    return (
        <div className="p-4 border-t border-border">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                Documentos
            </p>

            {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando documentos...
                </div>
            ) : docs.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">Nenhum documento enviado.</p>
            ) : (
                <ul className="space-y-2 mb-4">
                    {docs.map((doc) => (
                        <li
                            key={doc.id}
                            className="flex items-center justify-between gap-3 rounded-md border border-gray-100 bg-card px-3 py-2 text-sm"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                <span className="truncate text-foreground font-medium">
                                    {doc.filename || "Documento"}
                                </span>
                                <span className="text-xs text-muted-foreground shrink-0">
                                    {formatDate(doc.created_at)}
                                </span>
                                <span className="text-xs text-muted-foreground shrink-0 uppercase">
                                    {doc.tipo_doc}
                                </span>
                                {doc.tipo_grupo === "assinatura_manual" && (
                                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-statusWarningBg text-amber-600 border border-statusWarning/20 shrink-0">
                                        Manual
                                    </span>
                                )}
                                {(doc.crm_sent_at || sentIds.has(doc.id)) && (
                                    <span title={doc.crm_sent_at ? `Enviado ao CRM em ${formatDate(doc.crm_sent_at)}` : "Enviado ao CRM"}>
                                        <CloudCheck className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                                    </span>
                                )}
                            </div>
                            <Button
                                variant="ghost"
                                size="sm"
                                title="Download"
                                onClick={() => handleDownload(doc.id)}
                                className="shrink-0"
                            >
                                <Download className="h-4 w-4 text-muted-foreground" />
                            </Button>
                        </li>
                    ))}
                </ul>
            )}

            {!loading && (
                <div className="flex flex-wrap items-center gap-2 mt-2">
                    {!crmCardId ? (
                        <Button
                            size="sm"
                            variant="outline"
                            className="border-statusSuccess/30 text-statusSuccess hover:bg-statusSuccessBg gap-1.5"
                            disabled={crmLoading}
                            onClick={handleCriarCard}
                        >
                            {crmLoading
                                ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                : <Plus className="h-3.5 w-3.5" />
                            }
                            Criar task no CRM
                        </Button>
                    ) : (
                        <>
                            {moveStatuses.length > 0 && (
                                <Select
                                    value={selectedStatus || "__none__"}
                                    onValueChange={v => setSelectedStatus(v === "__none__" ? "" : v)}
                                >
                                    <SelectTrigger className="h-8 text-xs w-40 shrink-0">
                                        <SelectValue placeholder="Sem mover status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none__">Sem mover status</SelectItem>
                                        {moveStatuses.map(([k, v]) => (
                                            <SelectItem key={k} value={v}>{k}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            )}
                            <Button
                                size="sm"
                                variant="outline"
                                className="border-statusInfo/30 text-statusInfo hover:bg-statusInfoBg gap-1.5"
                                disabled={crmLoading || docs.length === 0}
                                onClick={handleEnviarDocs}
                            >
                                {crmLoading
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    : <Send className="h-3.5 w-3.5" />
                                }
                                Enviar docs ao CRM
                            </Button>
                        </>
                    )}
                </div>
            )}

            {crmMsg && (
                <div className={`mt-3 flex items-center gap-1.5 text-xs font-medium ${crmMsg.type === "success" ? "text-green-700" : "text-red-600"}`}>
                    {crmMsg.type === "success"
                        ? <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                        : <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                    }
                    {crmMsg.text}
                </div>
            )}

            <ConfirmDialog
                isOpen={showStatusConfirm}
                onOpenChange={setShowStatusConfirm}
                title="Remover de Análise?"
                description="Os documentos foram enviados com sucesso. Deseja remover o substituído do status 'Em Análise' e retorná-lo para 'Pendente'?"
                confirmText="Sim, remover"
                cancelText="Manter em análise"
                onConfirm={handleConfirmStatusChange}
                isLoading={isUpdatingStatus}
                icon="help"
            />
        </div>
    );
}
